import { useState } from 'react';
import { api } from '../../services/api';
import './RelatedSlotActions.css';

interface Candidate {
  word: string;
  definition: string;
  relationship: string;
  bucket_proposed?: string | null;
  vet_verdict?: string | null;
  game_worthy?: boolean;
  reason?: string | null;
  error?: string;
}

/**
 * "Make this a Game Word" for one of Stage 1's three related-word slots.
 *
 * Promoting sends the word to the Word Bank as Game/odin/approved, which fires
 * the existing Game approve chain for it, then asks for three replacement
 * candidates for the vacated slot.
 *
 * NOTHING IS AUTO-FILLED (author ruling 2026-09-13). The three candidates are
 * shown with their proposed bucket and a one-line reason; Odin picks one, or
 * asks for three more against the same exclusion list plus the three he just
 * rejected.
 */
export default function RelatedSlotActions({
  parentId, slot, word, onSlotChanged,
}: {
  parentId: string;
  slot: 1 | 2 | 3;
  word: string;
  onSlotChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exclusionCount, setExclusionCount] = useState<number | null>(null);

  const fetchCandidates = async (rejectedSoFar: string[]) => {
    setBusy(true);
    setError(null);
    try {
      const res = (await api.post(`/api/wordbank/${parentId}/slot/${slot}/candidates`, {
        rejected: rejectedSoFar,
      })) as { candidates: Candidate[]; exclusion?: { count: number } };
      setCandidates(res.candidates || []);
      setExclusionCount(res.exclusion?.count ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const promote = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = (await api.post(`/api/wordbank/${parentId}/promote-to-game`, {
        word,
        note: `promoted from Stage 1 slot ${slot} of this word`,
      })) as { chained?: boolean };
      // Stage 1 runs only when the auto-chain switch in Settings is on.
      setNote(res.chained
        ? `"${word}" promoted to Game and queued for its own Stage 1.`
        : `"${word}" promoted to Game. No Stage 1 started (auto-chain is off).`);
      await fetchCandidates([]);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const choose = async (c: Candidate) => {
    setBusy(true);
    try {
      await api.post(`/api/wordbank/${parentId}/slot/${slot}/choose`, {
        word: c.word, definition: c.definition, relationship: c.relationship,
      });
      setCandidates(null);
      setRejected([]);
      setNote(`Slot ${slot} is now "${c.word}".`);
      onSlotChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const askForMore = async () => {
    const next = [...rejected, ...(candidates || []).map((c) => c.word)];
    setRejected(next);
    await fetchCandidates(next);
  };

  if (!word) return null;

  return (
    <div className="rsa">
      {!candidates && (
        <button className="rsa-promote" onClick={promote} disabled={busy}>
          {busy ? 'Working…' : 'Make this a Game Word'}
        </button>
      )}
      {note && <div className="rsa-note">{note}</div>}
      {error && <div className="rsa-error">{error}</div>}

      {candidates && (
        <div className="rsa-candidates">
          <div className="rsa-head">
            Pick a replacement for slot {slot}
            {exclusionCount !== null && (
              <span className="rsa-excl" title="Every word in the bank, the parent, and the other slots">
                {exclusionCount} words excluded
              </span>
            )}
          </div>
          {candidates.length === 0 && <div className="rsa-note">No usable candidates came back. Ask for three more.</div>}
          {candidates.map((c) => (
            <div key={c.word} className="rsa-cand">
              <div className="rsa-cand-head">
                <span className="rsa-word">{c.word}</span>
                {c.bucket_proposed && (
                  <span className={`wb-chip wb-b-${c.bucket_proposed}`}>{c.bucket_proposed}</span>
                )}
                {c.game_worthy && <span className="rsa-hint" title="The scorer proposed this as a Game word">game-worthy</span>}
                <button className="rsa-pick" onClick={() => choose(c)} disabled={busy}>Use this</button>
              </div>
              <div className="rsa-def">{c.definition}</div>
              {c.reason && <div className="rsa-reason">{c.reason}</div>}
              {c.error && <div className="rsa-error">{c.error}</div>}
            </div>
          ))}
          <div className="rsa-more">
            <button className="rsa-pick" onClick={askForMore} disabled={busy}>
              {busy ? 'Working…' : 'Three more'}
            </button>
            <button className="rsa-pick" onClick={() => { setCandidates(null); setRejected([]); }} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
